import { cn } from '@/utils/ui/styles'

import { Card } from '@/components/ui/card'

type KpiCardProps = {
	label: string
	/**
	 * `null` is the no-data state — deliberately not a zero, which is a value.
	 * `undefined` is excluded so an optional field can't slip through and render
	 * a silently blank headline instead: the caller has to spell out `?? null`.
	 */
	value: Exclude<React.ReactNode, undefined>
	subtitle: string
	/** Error treatment (red) — e.g. Needs attention only when count > 0. */
	alarm?: boolean
	className?: string
}

/**
 * One tile of a KPI strip: uppercase label, a headline value and a caption.
 * Lives here rather than in the integrations feature so a second strip can
 * render it without crossing a feature boundary — the settings-card resolution
 * under A-02.
 *
 * Both headline branches carry `data-slot="kpi-value"` — deliberately the SAME
 * slot for the measured and the no-data treatment, so "this tile reports a
 * figure" is one text assertion on one node that always resolves. Split slots
 * would let a tile that stopped rendering either branch pass a not-"No data"
 * check by being absent. The demo smoke asserts on it
 * (`e2e/specs-demo/hero-flow.spec.ts`).
 */
export const KpiCard = ({
	label,
	value,
	subtitle,
	alarm = false,
	className,
}: KpiCardProps) => {
	return (
		<Card
			className={cn(
				// Card's base `ring-1` supplies the width; alarm only recolors it red.
				'gap-2 p-4',
				alarm && 'ring-destructive/60 bg-destructive/5',
				className,
			)}
		>
			<p
				className={cn(
					'text-xs font-medium tracking-wide uppercase',
					alarm ? 'text-destructive-text' : 'text-muted-foreground',
				)}
			>
				{label}
			</p>
			{/* Fixed to the headline's own line box (`text-3xl`/`leading-none`) so
			    the no-data treatment, which is deliberately smaller, still occupies
			    the same height. Without it a strip mixing measured and absent
			    metrics steps its captions up and down across the row. */}
			<div className="flex h-[1.875rem] items-center">
				{value === null ? (
					// Deliberately not the headline treatment: an absent metric must not
					// look like a measured one.
					<p
						data-slot="kpi-value"
						className="text-muted-foreground text-xl leading-none font-medium"
					>
						No data
					</p>
				) : (
					<p
						data-slot="kpi-value"
						className="text-3xl leading-none font-semibold"
					>
						{value}
					</p>
				)}
			</div>
			<p className="text-muted-foreground text-sm">{subtitle}</p>
		</Card>
	)
}

/**
 * `KpiCard`'s resolved height, measured in the browser rather than derived from
 * the source: p-4 (32) + two gap-2 (16) + the `text-xs` label line (16) + the
 * fixed 1.875rem headline box (30) + the `text-sm` caption (20).
 *
 * Exported from here, beside the component it measures, because three strips'
 * skeletons need it and a copy per strip is how one of them ends up 10px short —
 * which is exactly what happened to the integrations list's. The skeleton that
 * consumes it lives in `components/skeletons/` per A-03; only the measurement
 * belongs next to the thing measured.
 */
export const KPI_CARD_HEIGHT_CLASS = 'h-[114px]'
